import { useEffect, useState } from 'react'
import { useCallback, useMemo } from 'react'

import { useRouter } from 'next/router'

import { indexes } from '@mab-common/utils'

import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import RangeSlider from '~/components/atoms/RangeSlider/RangeSlider'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import type { TSearchAnalystEventProps } from '~/services/elastic/analyst'
import useSearchAnalyst from '~/services/elastic/analyst'

import styles from './Filters.module.css'
import EsIndexTypes = indexes.EsIndexTypes
import { multiPostHogEventPusher } from '~/utils/analyticsPusher'

interface FilterRangeProps {
  title?: string
  changeHandler: (x: any) => void
  closeFilters: () => void
  prices: [number]
  testID?: string
  searchInsightsEvent?: TSearchAnalystEventProps
  filterType?: string
  selectedPrices?: number[]
  testAttr?: string
}

const FilterRange = ({
  title,
  prices,
  changeHandler,
  testID,
  searchInsightsEvent,
  selectedPrices,
  filterType,
  closeFilters,
  testAttr,
}: FilterRangeProps) => {
  let start = Math.min(...prices)
  start = start < 0 ? 0 : start
  const end = Math.max(...prices)

  const router = useRouter()

  useEffect(() => {
    if (router.query?.prices) {
      const filterPrices = JSON.parse(String(router.query?.prices))
      setPriceRange(filterPrices)
    } else {
      setPriceRange([])
    }
  }, [router.query?.prices])

  const [priceRange, setPriceRange] = useState(selectedPrices)

  const { sendSearchInsightsEvent } = useSearchAnalyst()

  const eventFilterType = useMemo(
    () => (filterType === 'genre' ? 'genres' : filterType),
    [filterType]
  )

  const handlePriceChange = (range: any) => {
    setPriceRange(range)
  }

  const clearPriceRange = () => {
    handlePriceChange([])
    changeHandler([])
    closeFilters?.()
  }

  const submitPriceChange = useCallback(async () => {
    changeHandler(priceRange)

    if (searchInsightsEvent && priceRange) {
      sendSearchInsightsEvent(searchInsightsEvent.method, {
        indexType: EsIndexTypes.ARTWORKS,
        eventName: searchInsightsEvent.name,
        filters: [
          `${eventFilterType}._id:${priceRange[0]}`,
          `${eventFilterType}._id:${priceRange[1]}`,
        ],
      })
    }

    if (priceRange) {
      await multiPostHogEventPusher([
        {
          eventType: `${searchInsightsEvent?.name || 'Price filter changed'}`,
        },
      ])
    }
  }, [searchInsightsEvent?.method, searchInsightsEvent?.name, priceRange])

  return (
    <div className={styles.filterList} data-testid={testID}>
      {title && (
        <Text
          size={TextSizes.primaryTitleLrg}
          Tag="h4"
          className={styles.filterListTitle}
        >
          {title}
        </Text>
      )}
      <RangeSlider
        value={priceRange}
        min={start}
        max={end}
        handleChange={handlePriceChange}
      />
      <div className={styles.buttons}>
        <Button
          variant={ButtonVariants.primaryDark}
          button={{
            type: ButtonTypes.button,
            onClick: submitPriceChange,
          }}
          testAttr="filter-range-submit"
        >
          Filter
        </Button>
        <Button
          variant={ButtonVariants.textLinkLightSmall}
          button={{
            type: ButtonTypes.button,
            onClick: clearPriceRange,
          }}
          testAttr="filter-range-clear"
        >
          Clear
        </Button>
      </div>
    </div>
  )
}

export default FilterRange
